import * as Sentry from '@sentry/react'
import { isYouthEnvironment, isYouthPath } from './config/youthEnvironment'

function detectEnvironment() {
  const hostname = window.location.hostname
  if (hostname.includes('digipalz.io')) return 'production'
  if (hostname.includes('digipalz.ca')) return 'production-ca'
  if (hostname.includes('firebaseapp.com') || hostname.includes('web.app'))
    return 'staging'
  return 'development'
}

const dsn = import.meta.env.VITE_SENTRY_DSN
const NON_ACTIONABLE_UPDATE_REJECTION_PATTERN =
  /Object Not Found Matching Id:\d+,\s*MethodName:update,\s*ParamCount:4/i
const NON_ERROR_REJECTION_PREFIX = 'Non-Error promise rejection captured with value:'
const META_WEBVIEW_NOISE_PATTERN =
  /Java object is gone|navigation_performance_logger_android|enableDidUserTypeOnKeyboardLogging|enableButtonsClickedMetaDataLogging/i
const STALE_ASSET_PRELOAD_PATTERN = /Unable to preload CSS for \/assets\//
const STALE_CHUNK_SYNTAX_PATTERN = /Unexpected token '<'/i
const CLARITY_SCRIPT_PATTERN = /clarity\.ms/i
const RECAPTCHA_DOUBLE_RENDER_PATTERN =
  /reCAPTCHA has already been rendered in this element/i
const AUDIO_PLAY_ABORT_PATTERN =
  /The play\(\) request was interrupted by a call to pause\(\)/i
const OTP_RATE_LIMIT_PATTERN =
  /too many verification codes|requested too many verification|Too many failed attempts/i
const OTP_HTTP_RATE_LIMIT_PATTERN =
  /sendEmailVerificationOTP|verifyEmailOTP/i

function getExceptionFrames(event) {
  return (
    event?.exception?.values?.flatMap((value) => value?.stacktrace?.frames ?? []) ??
    []
  )
}

function isClarityThirdPartyError(event) {
  return getExceptionFrames(event).some((frame) =>
    CLARITY_SCRIPT_PATTERN.test(frame?.filename || '')
  )
}

function getEventPath(event) {
  const url = event?.request?.url
  if (!url) return window.location.pathname
  try {
    return new URL(url, window.location.origin).pathname
  } catch {
    return window.location.pathname
  }
}

// Child and student pages: keep the stack trace, drop anything identifying.
function scrubYouthEvent(event) {
  delete event.user
  if (event.request) {
    delete event.request.headers
    delete event.request.cookies
  }
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.filter(
      (crumb) => crumb?.category !== 'console' && crumb?.category !== 'ui.input'
    )
  }
  event.tags = { ...(event.tags || {}), youth_environment: 'true' }
  return event
}

if (dsn) {
  Sentry.init({
    dsn,
    environment: detectEnvironment(),
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    sendDefaultPii: false,
    denyUrls: [/clarity\.ms/i, /scripts\.clarity\.ms/i],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration()
    ],
    tracesSampleRate: detectEnvironment() === 'development' ? 1.0 : 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: isYouthEnvironment() ? 0 : 1.0,
    beforeSend(event, hint) {
      const originalException = hint?.originalException
      const originalMessage =
        typeof originalException === 'string'
          ? originalException
          : originalException?.message
      const eventMessage =
        event?.message || event?.exception?.values?.[0]?.value || ''
      const combinedMessage = `${originalMessage || ''} ${eventMessage}`.trim()

      if (
        combinedMessage.includes(NON_ERROR_REJECTION_PREFIX) &&
        NON_ACTIONABLE_UPDATE_REJECTION_PATTERN.test(combinedMessage)
      ) {
        return null
      }

      if (META_WEBVIEW_NOISE_PATTERN.test(combinedMessage)) {
        return null
      }

      if (STALE_ASSET_PRELOAD_PATTERN.test(combinedMessage)) {
        return null
      }

      if (STALE_CHUNK_SYNTAX_PATTERN.test(combinedMessage)) {
        return null
      }

      if (RECAPTCHA_DOUBLE_RENDER_PATTERN.test(combinedMessage)) {
        return null
      }

      if (AUDIO_PLAY_ABORT_PATTERN.test(combinedMessage)) {
        return null
      }

      if (OTP_RATE_LIMIT_PATTERN.test(combinedMessage)) {
        return null
      }

      if (
        OTP_HTTP_RATE_LIMIT_PATTERN.test(combinedMessage) &&
        /\b(429|423)\b/.test(combinedMessage)
      ) {
        return null
      }

      if (isClarityThirdPartyError(event)) {
        return null
      }

      const frames = getExceptionFrames(event)
      if (
        frames.some((frame) =>
          META_WEBVIEW_NOISE_PATTERN.test(frame?.filename || '')
        )
      ) {
        return null
      }

      if (isYouthPath(getEventPath(event))) {
        return scrubYouthEvent(event)
      }

      return event
    }
  })
}
